import { _, helpers, hooks, mui, React, ts, ui } from '_core';

import {
  getSignalsInfo,
  prepareStatisticsVsTimeData,
  prepareStatisticsVsTimeLinear,
  prepareStatisticsVsTimeScatter,
} from './helpers';
import { Params } from './types';

type SignalsInfo = { handle: string; realHandle: string; theme: string }[];

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  file: string;
  fullScreen: boolean;
  definition: ts.types.signal.Lasso;
  chartType: 'scatter' | 'linear';
  params: Params;
  title: string;
};

const Chart = ({
  id,
  readOnly,
  loadWidgetData,
  fullScreen,
  params,
  file,
  chartType,
  definition,
  title,
}: ChartProps) => {
  const [data, setData] = React.useState<ts.types.widgets.TableData>(null);

  const [error, setError] = React.useState<ts.types.common.ApiError>(null);

  const [selectedThemeTab, setSelectedThemeTab] = React.useState<string>();
  const [themesDisplayTab, setThemesDisplayTab] = React.useState<string[]>();

  const [selectedSignalTab, setSelectedSignalTab] = React.useState<string>();
  const [signalsDisplayTab, setSignalsDisplayTab] = React.useState<string[]>();

  const [selectedVariable, setSelectedVariable] = React.useState<string>();

  const [signalsInfo, setSignalsInfo] = React.useState<SignalsInfo>();

  const updateThemeTabs = (localSignalsInfo: SignalsInfo) => {
    const themesTabs = _.uniq(localSignalsInfo.map((el) => el.theme));

    setThemesDisplayTab(themesTabs);
    setSelectedThemeTab(themesTabs[0]);
  };

  const loadData = async () => {
    try {
      const response = await loadWidgetData({
        data: { file },
      });

      if (response.data) {
        const newData = prepareStatisticsVsTimeData(response.data, params.statisticType);
        setData(newData.data);

        const signalsInfo = getSignalsInfo(newData.variables, definition?.signals);
        setSignalsInfo(signalsInfo);

        updateThemeTabs(signalsInfo);
      }
    } catch (err) {
      setError(err as ts.types.common.ApiError);
    }
  };

  React.useEffect(() => {
    if (file) loadData();
    return () => {
      setData(null);
      setData(null);
    };
  }, [file]);

  hooks.useEffectWithoutFirst(() => {
    const signalsTabs = signalsInfo.filter((el) => el.theme == selectedThemeTab).map((el) => el.handle);

    setSignalsDisplayTab(signalsTabs);
    setSelectedSignalTab(signalsTabs[0]);
  }, [selectedThemeTab]);

  hooks.useEffectWithoutFirst(() => {
    const realHandle = signalsInfo.find((el) => el.handle == selectedSignalTab).realHandle;
    setSelectedVariable(realHandle);
  }, [selectedSignalTab]);

  const statisticTypeLabel = params.statisticType == 'regression' ? 'Regression Coefficient' : params.statisticType;

  const chartName = React.useMemo(() => {
    if (!selectedVariable) return null;
    const key = selectedVariable.replace('tstats|', '').replace('pvalues|', '');
    let chartName;
    if (key.includes('var')) chartName = helpers.buildName.getSignalDef(key.replace('var', ''), definition);

    return chartName;
  }, [selectedVariable]);

  const tabData = React.useMemo(() => {
    if (!data || !selectedVariable) return null;
    if (chartType == 'linear') return prepareStatisticsVsTimeLinear(data, selectedVariable);

    return prepareStatisticsVsTimeScatter(data, params.statisticType, selectedVariable);
  }, [data, chartType, params.statisticType, selectedVariable]);

  const renderChart = () => {
    if (chartType == 'linear') {
      return (
        <ui.LineChart
          id={id}
          data={tabData as ts.types.widgets.TableData}
          fullScreen={fullScreen}
          yAxisLabel={params.overall_stats ? selectedVariable : statisticTypeLabel}
          alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
          title={params.overall_stats ? null : chartName}
          dotted={['Q1', 'Q2', 'Q4', 'Q5']}
          exportTitle={title}
          readOnly={readOnly}
        />
      );
    }

    return (
      <ui.ScatterPlotChart
        id={id}
        fullScreen={fullScreen}
        readOnly={readOnly}
        data={tabData as ts.types.widgets.ScatterData}
        alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
        xAxisField="date"
        yAxisField={params.statisticType}
        xAxisLabel="Date"
        yAxisLabel={params.overall_stats ? selectedVariable : statisticTypeLabel}
        exportTitle={title}
        numberFormat="#.####"
        title={params.overall_stats ? null : chartName}
        hideLegend
        xIsDate
      />
    );
  };

  const getTabs = () => {
    if (themesDisplayTab) {
      const tabLabelRenderer = (tabLabel: string) => (
        <mui.core.Tooltip title={tabLabel} arrow>
          <div
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
            }}
          >
            {tabLabel}
          </div>
        </mui.core.Tooltip>
      );

      return (
        <>
          <mui.core.Tabs
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            value={selectedThemeTab}
            onChange={(_e, newTab) => setSelectedThemeTab(newTab)}
          >
            {themesDisplayTab.map((tab) => (
              <mui.core.Tab key={tab} label={tabLabelRenderer(tab)} value={tab} />
            ))}
          </mui.core.Tabs>
          {signalsDisplayTab?.length > 1 && (
            <mui.core.Tabs
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              value={selectedSignalTab}
              onChange={(_e, newTab) => setSelectedSignalTab(newTab)}
            >
              {signalsDisplayTab.map((tab) => (
                <mui.core.Tab key={tab} label={tabLabelRenderer(tab)} value={tab} />
              ))}
            </mui.core.Tabs>
          )}
        </>
      );
    }
    return null;
  };

  return (
    <mui.core.Box display="flex" flexDirection="column" style={{ height: '100%' }}>
      {getTabs()}
      <mui.core.Box p={3} flex="1">
        {renderChart()}
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default Chart;
